import React, { useCallback, useState } from 'react';
import {
  Button,
  ScrollableViewport,
  FormSearchWithDropdown,
} from '@ihsm/ui-shared';
import { ListWithItemDrag } from 'shared/components/ListWithRowDrag';
import { NO_BENCHMARK } from 'state/store/constants';
import { DraggableItemWithDropdown } from './DraggableItemWithDropdown';
import { PortfolioItem } from '../types';
import './RunReportPortfoliosList.scss';
import { getPortfolioOptions } from '../utils';

interface RunReportPortfoliosListProps {
  data: PortfolioItem[];
  options: string[];
  onDataChange: (data: PortfolioItem[]) => void;
  isDisabled?: boolean;
}

export const RunReportPortfoliosList: React.FC<
  RunReportPortfoliosListProps
> = ({ data = [], onDataChange = () => {}, isDisabled = false, options }) => {
  const portfoliosList = getPortfolioOptions(data);
  const [search, setSearch] = useState<string>();

  const handleDeletion = useCallback(
    (index: number) => {
      const updated = [...data].filter((_, i) => i !== index);
      onDataChange(updated);
    },
    [data, onDataChange]
  );

  const handleUpdateOption = useCallback(
    (value: string, index: number) => {
      const updated = [...data];
      updated[index].benchmarkName = value;
      onDataChange(updated);
    },
    [data, onDataChange]
  );

  const handleDataChange = useCallback(
    (newData: PortfolioItem[]) => {
      onDataChange(newData);
    },
    [onDataChange]
  );

  const handleAddNew = useCallback(() => {
    if (search && search !== '') {
      const selected = portfoliosList.find((item) => item === search);

      if (selected) {
        onDataChange([selected, ...data]);
        setSearch('');
      }
    }
  }, [data, onDataChange, portfoliosList, search]);

  const handleSearch = (value?: string) => {
    setSearch(value);
  };

  return (
    <div className="run-report-portfolio-list">
      <FormSearchWithDropdown
        searchInput={search}
        setSearchInput={setSearch}
        label="Portfolios"
        onSearch={handleSearch}
        disabled={isDisabled}
        optionsList={portfoliosList}
        actionButton={
          <Button text="+" onClick={handleAddNew} disabled={isDisabled} />
        }
      />

      <div className="run-report-portfolio-list__list-box">
        <ScrollableViewport vertical>
          <ListWithItemDrag
            keyExpr="text"
            data={data}
            onDataChange={handleDataChange}
            disabled={isDisabled}
            itemTemplate={(props: PortfolioItem, index: number) => (
              <DraggableItemWithDropdown
                text={props.text}
                selectedOption={props.benchmarkName ?? NO_BENCHMARK}
                options={options}
                onChangeOption={(value) => handleUpdateOption(value, index)}
                onRemove={() => handleDeletion(index)}
              />
            )}
          />
        </ScrollableViewport>
      </div>
    </div>
  );
};
